import { WatchlistItem } from './store';

export interface WatchStats {
  totalItems: number;
  totalTVShows: number;
  totalMovies: number;
  totalSeasonsWatched: number;
  totalEpisodesWatched: number;
  estimatedHoursWatched: number;
  completedShows: number;
  activeShows: number;
  watchedMovies: number;
  unwatchedMovies: number;
  averageRating: number;
  ratedItems: number;
}

export interface GenreDistribution {
  genreId: number;
  genreName: string;
  count: number;
  percentage: number;
}

/**
 * Calculate comprehensive watch statistics from user's watchlist
 */
export function calculateWatchStats(watchlist: WatchlistItem[]): WatchStats {
  const tvShows = watchlist.filter(item => item.type === 'tv');
  const movies = watchlist.filter(item => item.type === 'movie');

  // Calculate movie stats first
  const watchedMoviesCount = movies.filter(movie => movie.watched === true).length;
  const unwatchedMovies = movies.filter(movie => !movie.watched).length;

  // Calculate TV show stats
  const totalSeasonsWatched = tvShows.reduce(
    (sum, show) => sum + show.watchedSeasons.length,
    0
  );

  // UPDATED: Count actual watched episodes instead of estimating
  const totalEpisodesWatched = tvShows.reduce((sum, show) => {
    if (show.watchedEpisodes) {
      // Count episodes from watchedEpisodes
      return sum + Object.values(show.watchedEpisodes).reduce(
        (seasonSum, episodes) => seasonSum + episodes.length,
        0
      );
    } else {
      // Fallback to old estimation if no episode data
      return sum + (show.watchedSeasons.length * 10);
    }
  }, 0);

  // Calculate hours (45 min per episode for TV, 2 hours per movie)
  const tvHoursWatched = (totalEpisodesWatched * 45) / 60;
  const movieHoursWatched = watchedMoviesCount * 2;
  const estimatedHoursWatched = tvHoursWatched + movieHoursWatched;

  const completedShows = tvShows.filter(
    show => show.totalSeasons && show.watchedSeasons.length >= show.totalSeasons
  ).length;

  const activeShows = tvShows.filter(
    show => show.currentlyWatching === true
  ).length;

  const ratedItems = watchlist.filter(item => item.personalRank).length;
  const averageRating = ratedItems > 0
    ? watchlist.reduce((sum, item) => sum + (item.personalRank || 0), 0) / ratedItems
    : 0;

  return {
    totalItems: watchlist.length,
    totalTVShows: tvShows.length,
    totalMovies: movies.length,
    totalSeasonsWatched,
    totalEpisodesWatched, // Now accurate!
    estimatedHoursWatched: Math.round(estimatedHoursWatched),
    completedShows,
    activeShows,
    watchedMovies: watchedMoviesCount,
    unwatchedMovies,
    averageRating: Math.round(averageRating * 10) / 10,
    ratedItems,
  };
}

/**
 * TMDb Genre ID to Name mapping
 */
const GENRE_MAP: Record<number, string> = {
  28: 'Action',
  12: 'Adventure',
  16: 'Animation',
  35: 'Comedy',
  80: 'Crime',
  99: 'Documentary',
  18: 'Drama',
  10751: 'Family',
  14: 'Fantasy',
  36: 'History',
  27: 'Horror',
  10402: 'Music',
  9648: 'Mystery',
  10749: 'Romance',
  878: 'Sci-Fi',
  10770: 'TV Movie',
  53: 'Thriller',
  10752: 'War',
  37: 'Western',
  10759: 'Action & Adventure',
  10762: 'Kids',
  10763: 'News',
  10764: 'Reality',
  10765: 'Sci-Fi & Fantasy',
  10766: 'Soap',
  10767: 'Talk',
  10768: 'War & Politics',
};

/**
 * Calculate genre distribution from watchlist
 */
export function calculateGenreDistribution(
  watchlist: WatchlistItem[],
  limit: number = 5
): GenreDistribution[] {
  const genreCounts: Record<number, number> = {};

  // Count all genres
  watchlist.forEach(item => {
    if (item.genres && item.genres.length > 0) {
      item.genres.forEach(genreId => {
        genreCounts[genreId] = (genreCounts[genreId] || 0) + 1;
      });
    }
  });

  // Convert to array and sort by count
  const distribution = Object.entries(genreCounts)
    .map(([genreId, count]) => {
      const id = parseInt(genreId);
      return {
        genreId: id,
        genreName: GENRE_MAP[id] || 'Unknown',
        count,
        percentage: (count / watchlist.length) * 100,
      };
    })
    .sort((a, b) => b.count - a.count)
    .slice(0, limit);

  return distribution;
}

/**
 * Calculate watch activity for a date range
 * Returns activity map with dates as keys
 */
export function calculateActivityCalendar(
  watchlist: WatchlistItem[],
  daysBack: number = 365
): Record<string, number> {
  const activityMap: Record<string, number> = {};
  const now = new Date();
  const startDate = new Date(now.getTime() - daysBack * 24 * 60 * 60 * 1000);

  watchlist.forEach(item => {
    // Count when item was added
    if (item.addedAt) {
      const addedDate = new Date(item.addedAt);
      if (addedDate >= startDate) {
        const dateKey = addedDate.toISOString().split('T')[0];
        activityMap[dateKey] = (activityMap[dateKey] || 0) + 1;
      }
    }

    // Count when item was last watched
    if (item.lastWatched) {
      const watchedDate = new Date(item.lastWatched);
      if (watchedDate >= startDate) {
        const dateKey = watchedDate.toISOString().split('T')[0];
        activityMap[dateKey] = (activityMap[dateKey] || 0) + 1;
      }
    }
  });

  return activityMap;
}

/**
 * Calculate current and longest watch streak
 */
export function calculateStreaks(watchlist: WatchlistItem[]): {
  currentStreak: number;
  longestStreak: number;
} {
  // Get all watch dates (addedAt, lastWatched, AND episodeWatchDates)
  const dates = new Set<string>();

  watchlist.forEach(item => {
    if (item.addedAt) {
      dates.add(new Date(item.addedAt).toISOString().split('T')[0]);
    }
    if (item.lastWatched) {
      dates.add(new Date(item.lastWatched).toISOString().split('T')[0]);
    }
    // ADD: Include episode watch dates
    if (item.episodeWatchDates) {
      Object.values(item.episodeWatchDates).forEach(dateStr => {
        dates.add(new Date(dateStr).toISOString().split('T')[0]);
      });
    }
  });

  const sortedDates = Array.from(dates).sort().reverse();

  if (sortedDates.length === 0) {
    return { currentStreak: 0, longestStreak: 0 };
  }

  let currentStreak = 0;
  let longestStreak = 0;
  let tempStreak = 1;

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const todayStr = today.toISOString().split('T')[0];

  // Calculate current streak (from today backwards)
  const mostRecent = new Date(sortedDates[0]);
  mostRecent.setHours(0, 0, 0, 0);
  const daysSinceLastActivity = Math.floor(
    (today.getTime() - mostRecent.getTime()) / (1000 * 60 * 60 * 24)
  );

  if (daysSinceLastActivity <= 1) {
    currentStreak = 1;
    for (let i = 1; i < sortedDates.length; i++) {
      const prevDate = new Date(sortedDates[i - 1]);
      const currDate = new Date(sortedDates[i]);
      const dayDiff = Math.floor(
        (prevDate.getTime() - currDate.getTime()) / (1000 * 60 * 60 * 24)
      );

      if (dayDiff === 1) {
        currentStreak++;
      } else {
        break;
      }
    }
  }

  // Calculate longest streak
  for (let i = 1; i < sortedDates.length; i++) {
    const prevDate = new Date(sortedDates[i - 1]);
    const currDate = new Date(sortedDates[i]);
    const dayDiff = Math.floor(
      (prevDate.getTime() - currDate.getTime()) / (1000 * 60 * 60 * 24)
    );

    if (dayDiff === 1) {
      tempStreak++;
      longestStreak = Math.max(longestStreak, tempStreak);
    } else {
      tempStreak = 1;
    }
  }

  longestStreak = Math.max(longestStreak, 1);

  return { currentStreak, longestStreak };
}

/**
 * Format time duration for display
 */
export function formatWatchTime(hours: number): string {
  if (hours < 24) {
    return `${hours}h`;
  }

  const days = Math.floor(hours / 24);
  const remainingHours = hours % 24;

  if (remainingHours === 0) {
    return `${days}d`;
  }

  return `${days}d ${remainingHours}h`;
}

/**
 * Get member duration text
 */
export function getMemberDuration(joinDate: string): string {
  const join = new Date(joinDate);
  const now = new Date();
  const diffMs = now.getTime() - join.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays < 30) {
    return `${diffDays} day${diffDays !== 1 ? 's' : ''}`;
  } else if (diffDays < 365) {
    const months = Math.floor(diffDays / 30);
    return `${months} month${months !== 1 ? 's' : ''}`;
  } else {
    const years = Math.floor(diffDays / 365);
    return `${years} year${years !== 1 ? 's' : ''}`;
  }
}
